import { ReactNode } from "react";

import { Box } from "@mui/material";
import { ALL_VALUE, AllValueType, isAll } from "@bridgesplit/utils";

import { Column, Row } from "../../containers";
import { BORDER_RADIUS, useAppPalette } from "../../../theme";
import { Text, TextSkeleton } from "../../typography";
import { TableKey, TableRow } from "../types";

const CHIP_SKELETON_COUNT = 4;

interface FilterCarouselProps<TGroup> {
    groups: TableRow<TGroup>[];
    activeFilter: AllValueType | TableKey;
    onFilterChange: (next: AllValueType | TableKey) => void;
    minWidth: number;
    renderChip: (row: TableRow<TGroup> | undefined, index: number) => ReactNode;
    allCard: ReactNode;
    loading?: boolean;
}

const SCROLL_SX = {
    overflowX: "auto",
    mx: -1,
    px: 1,
    "::-webkit-scrollbar": { display: "none" },
    scrollbarWidth: "none"
} as const;

const ROW_SX = { gap: 1, flexWrap: "nowrap", alignItems: "stretch" } as const;

export function FilterCarousel<TGroup>({
    groups,
    activeFilter,
    onFilterChange,
    minWidth,
    renderChip,
    allCard,
    loading
}: FilterCarouselProps<TGroup>) {
    return (
        <Box sx={SCROLL_SX}>
            <Row sx={ROW_SX}>
                {loading ? (
                    Array.from({ length: CHIP_SKELETON_COUNT }).map((_, i) => (
                        <ChipShell key={i} minWidth={minWidth}>
                            {renderChip(undefined, i)}
                        </ChipShell>
                    ))
                ) : (
                    <>
                        <ChipShell
                            active={isAll(activeFilter)}
                            minWidth={minWidth}
                            onClick={() => onFilterChange(ALL_VALUE)}
                        >
                            {allCard}
                        </ChipShell>
                        {groups.map((row, i) => (
                            <ChipShell
                                key={row.key}
                                active={activeFilter === row.key}
                                minWidth={minWidth}
                                onClick={() => onFilterChange(row.key)}
                            >
                                {renderChip(row, i)}
                            </ChipShell>
                        ))}
                    </>
                )}
            </Row>
        </Box>
    );
}

function ChipShell({
    active,
    minWidth,
    onClick,
    children
}: {
    active?: boolean;
    minWidth: number;
    onClick?: () => void;
    children: ReactNode;
}) {
    const { primary, primaryToAlpha, border, cardBackground } = useAppPalette();
    return (
        <Column
            spacing={1}
            onClick={onClick}
            sx={{
                flexShrink: 0,
                minWidth,
                cursor: onClick ? "pointer" : "default",
                p: 1.5,
                borderRadius: BORDER_RADIUS,
                border: active ? `1px solid ${primary}` : border,
                backgroundColor: active ? primaryToAlpha : cardBackground
            }}
        >
            {children}
        </Column>
    );
}

export function ChipValue({ value, subtitle }: { value: ReactNode; subtitle: ReactNode }) {
    return (
        <Column spacing={0.25} sx={{ minWidth: 0 }}>
            {value === undefined ? <TextSkeleton variant="h4" width="72px" /> : <Text variant="h4">{value}</Text>}
            {subtitle === undefined ? (
                <TextSkeleton variant="body2" width="96px" />
            ) : (
                <Text variant="body2" color="caption" sx={{ whiteSpace: "nowrap" }}>
                    {subtitle}
                </Text>
            )}
        </Column>
    );
}
